'use client';

import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { formatBengaliDateTime } from '@/lib/dateUtils';

export interface ShareCardArticle {
    id: number;
    title: string;
    slug: string;
    image_url: string;
    published_at: string | null;
    created_at: string;
    is_manual?: boolean;
}

interface ShareCardProps {
    article: ShareCardArticle;
    articleUrl: string;
    imageProxyUrl?: string;
    onImageLoad?: () => void;
    onImageError?: () => void;
    cardRef?: React.RefObject<HTMLDivElement | null>;
}

export default function ShareCard({
    article,
    articleUrl,
    imageProxyUrl,
    onImageLoad,
    onImageError,
    cardRef,
}: ShareCardProps) {
    const imgSrc =
        imageProxyUrl ??
        (typeof window !== 'undefined' && article.image_url
            ? `${window.location.origin}/api/proxy-image?url=${encodeURIComponent(article.image_url)}`
            : article.image_url);

    return (
        <div
            ref={cardRef}
            className="bg-slate-50 flex flex-col relative overflow-hidden font-bengali"
            style={{
                width: '1080px',
                minWidth: '1080px',
                height: '1080px',
                maxWidth: '1080px',
            }}
        >
            <div className="w-full h-[55%] relative bg-slate-200">
                {article.image_url && imgSrc && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={imgSrc}
                        alt={article.title}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        onLoad={onImageLoad}
                        onError={onImageError}
                    />
                )}
                <div className="absolute inset-0 bg-slate-800 mix-blend-multiply opacity-20" />
            </div>

            <div className="w-full h-[45%] flex flex-col bg-slate-50 relative z-10 shadow-[0_-10px_40px_rgba(0,0,0,0.1)]">
                <div className="flex-1 min-h-0 flex flex-col justify-center px-12 py-4" style={{ minWidth: '1080px' }}>
                    <div
                        className="inline-flex self-start px-5 py-2 bg-red-100 text-red-600 rounded-lg text-lg font-bold mb-3 tracking-wide mt-2"
                        style={{ whiteSpace: 'nowrap', flexShrink: 0, maxWidth: 'none' }}
                    >
                        {formatBengaliDateTime(
                            article.published_at || article.created_at,
                            !!article.is_manual
                        )}
                    </div>
                    <h1
                        className="text-[48px] font-black leading-[1.35] text-slate-900 tracking-tight line-clamp-4"
                        style={{ fontFamily: 'var(--font-noto-serif-bengali), serif' }}
                    >
                        {article.title}
                    </h1>
                </div>

                <div className="shrink-0 w-full border-t border-red-900/50 py-8 px-12 flex justify-between items-center bg-gradient-to-r from-red-900 via-slate-900 to-black text-white">
                    <div className="flex-1 flex flex-col gap-4">
                        <div className="flex items-center gap-5">
                            <h2
                                className="text-[54px] font-extrabold tracking-tight leading-none drop-shadow-md"
                                style={{ fontFamily: 'var(--font-hind-siliguri), sans-serif' }}
                            >
                                মুক্তকণ্ঠ<span className="text-red-500">.</span>
                            </h2>
                            <div className="px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-red-100 rounded-full text-[18px] font-bold tracking-wider">
                                muktokontho.org
                            </div>
                        </div>
                        <p className="text-[22px] font-medium leading-[1.6] text-slate-300">
                            Open Journalism for Everyone | Protecting Truth Respecting Voices
                        </p>
                    </div>
                    <div className="flex items-center gap-6 shrink-0 pl-8 border-l border-white/20">
                        <div className="p-4 bg-white border-2 border-red-500 rounded-3xl shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                            <QRCodeCanvas value={articleUrl} size={110} level="H" includeMargin={false} fgColor="#0f172a" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
